/**
 * Logique pure de recherche dans les menus de la sidebar.
 * Extrait de Sidebar.js pour être testable sans React et réutilisable
 * par la recherche unifiée (GlobalSearch).
 */

// Normalisation accent-insensible (minuscule + suppression diacritiques).
export const normalizeSearch = (s) =>
  (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Filtre cumulatif des items d'une section :
 * - si le label de section matche `q` → tous les items,
 * - sinon → seuls les items dont le label matche.
 * `q` DOIT déjà être normalisé. Prend en compte customLabels.
 */
export function applyMenuSearchFilter(items, cat, q, customLabels) {
  if (!q) return items;
  const sectionLabel = customLabels?.[`cat:${cat.id}`] || cat.label;
  if (normalizeSearch(sectionLabel).includes(q)) return items;
  return items.filter(item => {
    const label = customLabels?.[item.path] || item.label;
    return normalizeSearch(label).includes(q);
  });
}

/**
 * Aplatit les catégories en une liste plate des menus correspondant à `rawQuery`.
 * Les prédicats `isCategoryAllowed` et `filterItems` permettent à l'appelant
 * (Sidebar) d'injecter le filtrage par org / rôle / permission SANS le dupliquer ici.
 *
 * @returns Array<{kind:'menu', path, label, icon, sectionLabel}>
 */
export function flattenMenuResults(categories, rawQuery, opts = {}) {
  const {
    customLabels = {},
    isCategoryAllowed = () => true,
    filterItems = (items) => items,
  } = opts;
  const q = normalizeSearch(rawQuery);
  if (!q) return [];
  const out = [];
  for (const cat of categories) {
    if (!isCategoryAllowed(cat)) continue;
    const allowed = filterItems(cat.items);
    const matched = applyMenuSearchFilter(allowed, cat, q, customLabels);
    const sectionLabel = customLabels[`cat:${cat.id}`] || cat.label;
    for (const item of matched) {
      out.push({
        kind: 'menu',
        path: item.path,
        label: customLabels[item.path] || item.label,
        icon: item.icon,
        sectionLabel,
      });
    }
  }
  return out;
}
